/**
 * APPZ-3133 — K8s360 support for Windows nodes.
 *
 * Derives a Windows (windows_exporter) branch from an existing Linux (node_exporter / cAdvisor)
 * PromQL query and combines them with `or`. `or` drops right-hand series whose label set already
 * appears on the left, so the branches must stay disjoint: an aggregation with a `by` clause keeps
 * a per-node/per-pod label, while a label-less `sum(...)` collapses both branches to one `{}` series
 * and would silently discard the Windows one on a mixed cluster — for that shape the `or` is pushed
 * inside the sum (`sum((linux) or (windows))`). Any other label-less aggregate (`sum(a) / sum(b)`,
 * `avg(...)`) is out of scope and must be composed by the caller (see cluster.ts). On Linux-only
 * accounts the Windows branch returns no series — results are identical.
 */
const NODE_METRIC_MAP = new Map([
    ['node_cpu_seconds_total', 'windows_cpu_time_total'],
    ['node_memory_MemAvailable_bytes', 'windows_memory_available_bytes'],
    ['node_memory_MemTotal_bytes', 'windows_memory_physical_total_bytes'],
    ['node_filesystem_avail_bytes', 'windows_logical_disk_free_bytes'],
    ['node_filesystem_free_bytes', 'windows_logical_disk_free_bytes'],
    ['node_filesystem_size_bytes', 'windows_logical_disk_size_bytes'],
    ['node_disk_reads_completed_total', 'windows_logical_disk_reads_total'],
    ['node_disk_read_bytes_total', 'windows_logical_disk_read_bytes_total'],
    ['node_disk_read_time_seconds_total', 'windows_logical_disk_read_seconds_total'],
    ['node_disk_writes_completed_total', 'windows_logical_disk_writes_total'],
    ['node_disk_written_bytes_total', 'windows_logical_disk_write_bytes_total'],
    ['node_disk_write_time_seconds_total', 'windows_logical_disk_write_seconds_total'],
    ['node_network_transmit_bytes_total', 'windows_net_bytes_sent_total'],
    ['node_network_receive_bytes_total', 'windows_net_bytes_received_total'],
    ['node_network_transmit_packets_total', 'windows_net_packets_sent_total'],
    ['node_network_receive_packets_total', 'windows_net_packets_received_total'],
    ['node_network_transmit_drop_total', 'windows_net_packets_outbound_discarded_total'],
    ['node_network_receive_drop_total', 'windows_net_packets_received_discarded_total'],
]);
const CONTAINER_METRIC_MAP = new Map([
    ['container_cpu_usage_seconds_total', 'windows_container_cpu_usage_seconds_total'],
    // Private working set is the closest Windows analogue for both cAdvisor memory gauges.
    ['container_memory_working_set_bytes', 'windows_container_memory_usage_private_working_set_bytes'],
    ['container_memory_usage_bytes', 'windows_container_memory_usage_private_working_set_bytes'],
    ['container_network_transmit_bytes_total', 'windows_container_network_transmit_bytes_total'],
    ['container_network_receive_bytes_total', 'windows_container_network_receive_bytes_total'],
    ['container_network_transmit_packets_total', 'windows_container_network_transmit_packets_total'],
    ['container_network_receive_packets_total', 'windows_container_network_receive_packets_total'],
    ['container_network_transmit_packets_dropped_total', 'windows_container_network_transmit_packets_dropped_total'],
    ['container_network_receive_packets_dropped_total', 'windows_container_network_receive_packets_dropped_total'],
]);
// The Linux "used memory" composite (MemTotal - MemFree - Cached - Buffers) has no per-metric
// Windows mapping — those sites must compose an explicit Windows expression instead.
const UNMAPPED_NODE_MEMORY_RE = /node_memory_(?:MemFree|Cached|Buffers)_bytes/;
// Windows `interrupt`/`dpc` CPU time is already included in `privileged`, so the Linux-style
// `mode!="idle"` would double-count it on the Windows branch.
const LINUX_NON_IDLE_MODE_RE = /mode\s*!=\s*"idle"/g;
const WINDOWS_NON_IDLE_MODE = 'mode=~"user|privileged"';
// eslint-disable-next-line security/detect-non-literal-regexp -- built from the literal map keys above
const NODE_METRIC_RE = new RegExp(`\\b(?:${[...NODE_METRIC_MAP.keys()].join('|')})\\b`, 'g');
const buildWindowsNodeQuery = (query) => {
    const windowsQuery = query.replace(NODE_METRIC_RE, linuxMetric => NODE_METRIC_MAP.get(linuxMetric) ?? linuxMetric);
    return windowsQuery.includes('windows_cpu_time_total')
        ? windowsQuery.replace(LINUX_NON_IDLE_MODE_RE, WINDOWS_NON_IDLE_MODE)
        : windowsQuery;
};
const BARE_SUM_RE = /^\s*sum\s*\(([\s\S]*)\)\s*$/;
const hasBalancedParens = (expr) => {
    let depth = 0;
    for (const char of expr) {
        if (char === '(')
            depth += 1;
        else if (char === ')')
            depth -= 1;
        if (depth < 0)
            return false;
    }
    return depth === 0;
};
// `sum(<inner>)` with no `by` clause — the paren check rejects `sum(a) / sum(b)` and `sum(a) by (x)`.
const unwrapBareSum = (query) => {
    const inner = query.match(BARE_SUM_RE)?.[1];
    return inner !== undefined && hasBalancedParens(inner) ? inner : undefined;
};
// Unions the branches; a label-less `sum` wrapper is re-applied around the `or` (see header).
const combineBranches = (query, buildWindowsBranch) => {
    const inner = unwrapBareSum(query);
    const linux = inner ?? query;
    const windows = buildWindowsBranch(linux);
    if (!windows || windows === linux)
        return query;
    return inner === undefined ? `(${query}) or (${windows})` : `sum((${inner}) or (${windows}))`;
};
const withWindowsNodeFallback = (query) => {
    if (UNMAPPED_NODE_MEMORY_RE.test(query))
        return query;
    return combineBranches(query, buildWindowsNodeQuery);
};
/** Windows "used memory" bytes — the analogue of MemTotal - MemFree - Cached - Buffers. */
const buildWindowsMemoryUsedBytes = (filter) => `(windows_memory_physical_total_bytes{${filter}} - windows_memory_available_bytes{${filter}})`;
// `label_replace` regex is fully anchored; strips a runtime prefix (`containerd://<id>`) when
// present and is a no-op otherwise, so the join works whichever format each side reports.
const normalizeContainerId = (expr) => `label_replace(${expr}, "container_id", "$1", "container_id", "(?:.*://)?(.*)")`;
// windows_container_* series carry no pod/namespace labels (no cAdvisor on Windows) — pod identity
// is joined from kube_pod_container_info, so pod/namespace filters move to the info selector.
const splitContainerFilters = (filters) => {
    const kept = [];
    const info = [];
    filters
        .replace(/\s/g, '')
        .split(',')
        .filter(Boolean)
        .forEach(filter => {
        const label = filter.match(/^[a-zA-Z_][a-zA-Z0-9_]*/)?.[0];
        if (label === 'pod' || label === 'namespace') {
            // `pod!=""` is a cAdvisor artifact — kube_pod_container_info always has a pod label.
            if (filter !== 'pod!=""')
                info.push(filter);
        }
        else if (label === 'container' || label === 'image') {
            // Call sites only pass cAdvisor artifacts (`container=""`, `container!="POD"`, `image!=""`),
            // which are meaningless on the info metric — drop them. A positive container/image filter
            // would need to move to the info selector instead.
        }
        else if (label === 'instance' || label === 'node') {
            kept.push(filter.replace(/^(instance|node)/, 'kubernetes_node'));
        }
        else {
            kept.push(filter);
            if (label === 'p8s_logzio_name')
                info.push(filter);
        }
    });
    return { keptFilters: kept.join(', '), infoFilters: info.join(', ') };
};
const buildContainerJoin = ({ windowsMetric, filters, rangeFn, range, }) => {
    const { keptFilters, infoFilters } = splitContainerFilters(filters);
    const windowsVector = `${windowsMetric}{${keptFilters}}`;
    const windowsExpr = rangeFn ? `${rangeFn}(${windowsVector}[${range}])` : windowsVector;
    const infoExpr = `kube_pod_container_info{${infoFilters}}`;
    const onClause = 'on (p8s_logzio_name, container_id) group_left (pod, namespace)';
    return `${normalizeContainerId(windowsExpr)} * ${onClause} ${normalizeContainerId(infoExpr)}`;
};
const FUNC_VECTOR_RE = /\b(rate|irate|delta)\s*\(\s*(container_[a-z_]+)\s*\{([^}]*)\}\s*\[([^\]]*)\]\s*\)/g;
const BARE_VECTOR_RE = /\b(container_[a-z_]+)\s*\{([^}]*)\}/g;
// Only rate/irate/delta rewrites are supported. Any other function over a container range vector
// would fall through to BARE_VECTOR_RE, splicing the join inside its `[range]` — invalid PromQL
// that breaks the Linux branch too — so such queries must stay Linux-only untouched.
const RANGE_FN_VECTOR_RE = /\b([a-z_]+)\s*\(\s*container_[a-z_]+\s*\{[^}]*\}\s*\[/g;
const SUPPORTED_RANGE_FNS = new Set(['rate', 'irate', 'delta']);
const hasUnsupportedRangeFn = (query) => [...query.matchAll(RANGE_FN_VECTOR_RE)].some(([, rangeFn]) => !SUPPORTED_RANGE_FNS.has(rangeFn));
// Windows branch for a container expression, or undefined when the query must stay Linux-only:
// any unmapped metric (e.g. CFS throttling), unsupported range function, or no container vector.
const buildWindowsContainerQuery = (query) => {
    if (hasUnsupportedRangeFn(query))
        return undefined;
    let hasUnmappedMetric = false;
    let hasMappedMetric = false;
    const replaceVector = ({ match, metric, filters, rangeFn, range, }) => {
        const windowsMetric = CONTAINER_METRIC_MAP.get(metric);
        if (!windowsMetric) {
            hasUnmappedMetric = true;
            return match;
        }
        hasMappedMetric = true;
        return buildContainerJoin({ windowsMetric, filters, rangeFn, range });
    };
    const windowsQuery = query
        .replace(FUNC_VECTOR_RE, (match, ...groups) => {
        const [rangeFn, metric, filters, range] = groups;
        return replaceVector({ match, metric, filters, rangeFn, range });
    })
        .replace(BARE_VECTOR_RE, (match, ...groups) => {
        const [metric, filters] = groups;
        return replaceVector({ match, metric, filters });
    });
    return hasUnmappedMetric || !hasMappedMetric ? undefined : windowsQuery;
};
const withWindowsContainerFallback = (query) => combineBranches(query, buildWindowsContainerQuery);
// Exposed so coverage guard tests can assert every mapped Linux metric ships with its counterpart.
const WINDOWS_METRIC_EQUIVALENTS = new Map([...NODE_METRIC_MAP, ...CONTAINER_METRIC_MAP]);
export const k8sWindowsPromqlUtils = {
    withWindowsNodeFallback,
    withWindowsContainerFallback,
    buildWindowsNodeQuery,
    buildWindowsMemoryUsedBytes,
    windowsMetricEquivalents: WINDOWS_METRIC_EQUIVALENTS,
};
//# sourceMappingURL=k8s-windows-promql.util.js.map